import {
	Box,
	Card,
	CardContent,
	Grid,
	useMediaQuery
} from '@mui/material';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTheme } from '@mui/material/styles';
import strings from '../Globalization/index';
import LoadingDialog from '../components/common/LoadingDialog';
import NextPrevComponent from '../components/common/NextPrevComponent';
import consts from '../consts';
import { useSetState } from '../hooks';
import connectionService from '../services/Connection';
import util from '../util/index';
import { getSearchParams } from '../contexts/Prerequisites/RoutesGuard';
import XmlEditorDialog from '../contexts/CycleInfo/XmlEditorDialog';
import PrintProperties from './PrintProperties';
import { Receipt } from './cycleinfoviews';
import CIResultFormDialog from './sterilizercycleinfoviews/CIResultFormDialog';
import SterilizerCycleInfoProvider from './sterilizercycleinfoviews/Context';
import CriticalParameters from './sterilizercycleinfoviews/CriticalParameters';
import CycleBreadcrumbs from './sterilizercycleinfoviews/CycleBreadcrumbs';
import CycleReviewFormDialog from './sterilizercycleinfoviews/CycleReviewFormDialog';
import IncubatorResultDialog from './sterilizercycleinfoviews/IncubatorResultDialog';
import TopSection from './sterilizercycleinfoviews/TopSection';
import TrendsLineChart from './sterilizercycleinfoviews/TrendsLineChart';
import { getPrintDetailsParams } from './sterilizercycleinfoviews/dataUtil';

const printProperties = new PrintProperties();
const DTE_FIELDS = [
	'ActualConditionTime',
	'ActualSterilizeTime',
	'CreatedOn',
	'CycleDateTime',
	'CycleStart',
	'DryTime',
	'ModifiedOn',
	'SterilizeTimeActual',
	'SterilizeTimeMetric',
	'VHPExpirationActual',
	'ReviewedDate'
];

const SterilizerCycleInfo = () => {
	const comboTypes = [{ type: 'CycleActionStatus', loaded: true }];
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	let displayCycleTape = false;
	const screenWidth = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const is1024Width = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
	if (screenWidth || is1024Width) {
		displayCycleTape = true;
	}

	const { tags } = useSelector((sState) => sState.auth);

	const [state, setState] = useSetState({
		data: null,
		combos: {},
		loading: true,
		filters: {},
		isXmlProcess: false,
		ciResultFormDialogOpen: false,
		cycleReviewFormDialogOpen: false,
		biResultActiveRecord: null
	});
	const { STEAMEUSTERILIZER, AMSCOEUSTERILIZER } = consts.EquipmentFamilyType;
	const id = useMemo(
		() => (searchParams.has('id') ? searchParams.get('id') : state.data?._id),
		[searchParams, state.data]
	);
	const sterilizerId = useMemo(
		() => (searchParams.get('sterilizerId') || state.data?.SterilizerId),
		[searchParams, state.data]
	);
	const attachmentCount = useMemo(
		() => (searchParams.has('attachmentCount') ? searchParams.get('attachmentCount') : state.data?.AttachmentCount),
		[searchParams, state.data]
	);
	const cycleId = useMemo(
		() => (searchParams.has('cycleId') ? searchParams.get('cycleId') : state.data?.CycleId),
		[searchParams, state.data]
	);

	const isAMSCOEU = [AMSCOEUSTERILIZER].includes(state.data?.EquipmentFamilyId) || Boolean(searchParams.get('IsAMSCOEU'));
	const isSteamEU =
		[AMSCOEUSTERILIZER, STEAMEUSTERILIZER].includes(state.data?.EquipmentFamilyId) ||
		Boolean(searchParams.get('IsSteamEU'));
	const isLeakTestCycle = !!state.data?.MaxLeakRate;
	const isSteam =
		state.data?.EquipmentTypeId === consts.EquipmentType.STEAMSTERILIZER || Boolean(searchParams.get('isSteam'));
	const {
		loadRelease,
		access,
		isParametric,
		isBiometric,
		bIResultColor,
		bIResultIcon,
		cIResultColor,
		cIResultIcon,
		isLeakTest
	} = useMemo(() => {
		const loadRelease = !consts.isNodeServer
			? Number(util.localData?.LoadReleaseIdAccess || tags?.LoadReleaseId)
			: util.localData.LoadReleaseIdAccess || tags?.LoadReleaseId;
		const access = loadRelease === consts.LoadReleaseType.Parametric ? true : util.checkAccess('CycleApprovalAccess');
		const isParametric = loadRelease === consts.LoadReleaseType.Parametric;
		const isBiometric = loadRelease === consts.LoadReleaseType.Biological && !state.data?.ReviewedBy;
		const bIResultColor = state.data?.BIResultIcon
			? state.data.BIResultIcon.indexOf('red') > -1
				? 'red'
				: '#1ab394'
			: null;

		const bIResultIcon = state.data?.BIResultIcon
			? state.data?.BIResultIcon.indexOf('red') > -1
				? state.data?.BIResultIcon.replace('-red', '')
				: state.data?.BIResultIcon.replace('-green', '')
			: null;

		const cIResultColor = state.data?.CIResultIcon === 'fa-check' ? '#1ab394' : 'red';
		const cIResultIcon = state.data?.CIResultIcon;
		const isLeakTest = !!state.data?.MaxLeakRate;

		return {
			loadRelease,
			access,
			isParametric,
			isBiometric,
			bIResultColor,
			bIResultIcon,
			cIResultColor,
			cIResultIcon,
			isLeakTest
		};
	}, [state.data]);
	const tapeRef = useRef();
	const tapeContainerRef = useRef();
	const stockChartRef = useRef();
	const onOpenXmlModal = useCallback(() => {
		setState({ loading: true });
		const xmlEditorTitle = `${strings.XMLName} ${state.data.EquipmentTypeName}/${state.data.SerialNumber}`;
		const formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'PreviewXML');
		formData.append('cycleId', state.data.CycleId);
		fetch(connectionService.CycleList, {
			method: 'POST',
			body: formData
		})
			.then((res) => res.text())
			.then((xmlEditorValue) => {
				setState({ xmlEditorOpen: true, xmlEditorValue, loading: false, xmlEditorTitle });
			})
			.catch((error) => setState({ error }))
			.finally(() => setState({ loading: false }));
	}, [state.data]);

	const fetchData = useCallback(() => {
		setState({ loading: true });

		const formData = new FormData();
		formData.append('attachmentCount', attachmentCount);
		formData.append('sterilizerId', sterilizerId);
		formData.append('id', id);
		formData.append('action', 'load');
		formData.append('comboTypes', JSON.stringify(comboTypes));

		fetch(connectionService.SterilizerCycleList, { method: 'POST', body: formData })
			.then((res) => res.json())
			.then(({ data, combos }) => {
				DTE_FIELDS.forEach((field) => {
					data[field] = util.fixDates(data[field]);
				});

				setState({ loading: false, data, combos });
			})
			.catch((err) => {
				console.error(err)
				Swal.fire({
					titleText: strings.Error,
					text: strings[`Status_${err?.status}`],
					icon: 'error',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
			})
			.finally(() => setState({ loading: false }));
	}, [id, attachmentCount, sterilizerId]);

	const onOpenChartPopUp = (popupChartOpen) => setState({ popupChartOpen: popupChartOpen });

	const onPhasesFilter = (filters) => {
		setState({
			filters
		});
	};
	const onOpenCIResultFormDialog = () => setState({ ciResultFormDialogOpen: true });
	const onCloseCIResultFormDialog = (reload) => {
		setState({ ciResultFormDialogOpen: false });
		if (reload) {
			fetchData();
		}
	};

	const onOpenCycleReviewFormDialog = (approvalStatusId) =>
		setState({ cycleReviewFormDialogOpen: true, approvalStatusId });

	const onCloseCycleReviewFormDialog = (reload = false) => {
		setState({ cycleReviewFormDialogOpen: false });
		if (reload) {
			fetchData();
		}
	};

	const onCloseXmlEditorDialog = () =>
		setState({ xmlEditorOpen: false, xmlEditorValue: undefined, xmlEditorTitle: undefined });



	const getStockChartImageBase64Data = async () => {
		const element = stockChartRef.current;
		if (element) {
			const canvas = await html2canvas(element);
			const data = canvas.toDataURL('image/jpg');
			return data;
		}
		return;
	};


	const onPrintDetails = useCallback(async (preview = false) => {
		setState({ loading: true });
		const stockChart = await getStockChartImageBase64Data();
		const formData = new FormData();
		const params = getPrintDetailsParams({ data: state.data, isSteamEU, isAMSCOEU, isLeakTest });

		for (let { Name, Value } of params) {
			formData.set(Name, Value);
		}
		formData.set('action', 'otherAction');
		formData.set('otherAction', 'printReport');
		formData.set('cycleCount', state?.data?.CycleCount || 0);
		formData.set('imageUrl', state?.data?.SterilizerImageUrl);
		formData.set('cycleName', state?.data?.CycleName);
		formData.set('cycleTape', state?.data?.CycleTape);
		formData.set('isSteamEU', isSteamEU);
		formData.set('isAmscoEU', isAMSCOEU);
		formData.set('isLeakTest', isLeakTest);
		if (stockChart) {
			formData.set('stockChart', stockChart);
		}
		formData.set('noPhasesDataAvailable', strings.NoChartDataAvailableForCycle);
		fetch(connectionService.CycleList, { method: 'POST', body: formData })
			.then((res) => res.blob())
			.then((blob) => {
				if (blob instanceof Blob) {
					const filename = `${state.data.SerialNumber}_${state.data.CycleName.replace(/ /g, '')}_${state.data.CycleCount
						}_${dayjs(state.data.CycleStart).format(consts.MomentFormat.MonthDateYear)}`;
					const href = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = href;
					document.body.appendChild(link);
					if (preview) {
						link.setAttribute('target', `_blank`);
						link.setAttribute('type', `application/pdf`);
					} else {
						link.setAttribute('download', `${filename}.pdf`);
					}
					//
					link.click();
					document.body.removeChild(link);
				}
			})
			.catch((err) => {
				console.error(err);
				Swal.fire({
					titleText: strings.Error,
					text: strings[`Status_${err?.status}`] || err?.msg,
					icon: 'error',
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
			})
			.finally(() => setState({ loading: false }));
	}, [state.data, isSteamEU, isAMSCOEU, isLeakTest]);

	const handleOnLoadNextOrPrev = useCallback(
		({ id, equipmentId, type, isIncrement }) => {
			const formData = new FormData();
			setState({
				loading: true
			});
			const SerialNumber = state.data?.SerialNumber;
			equipmentId = equipmentId?._id || equipmentId;
			formData.append('action', 'otherAction');
			formData.append('otherAction', 'NextPrevHandler');
			formData.append('equipmentId', equipmentId);
			formData.append('id', id);
			formData.append('asArray', false);
			formData.append('type', type);
			formData.append('isIncrement', isIncrement);
			fetch(connectionService.CycleList, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					if (json.data?.length) {
						const data = json.data[0];
						const newId = data?._id;
						if (newId) {
							const searchParams = getSearchParams()
							searchParams.set('id', newId);
							searchParams.set('attachmentCount', data.AttachmentCount || 0);
							searchParams.set('sterilizerId', data.SterilizerCycleId || 0);
							navigate(`${location.pathname}?${searchParams.toString()}`);
						}
					} else {
						let message = isIncrement
							? strings.NextPage.replace('{0}', SerialNumber)
							: strings.PrevPage.replace('{0}', SerialNumber);
						Swal.fire({
							titleText: strings.Info,
							text: message,
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					}
				})
				.catch((err) => {
					console.error(err)
					Swal.fire({
						titleText: strings.Error,
						text: strings[`Status_${err?.status}`],
						icon: 'error',
						confirmButtonText: strings.Ok,
						allowOutsideClick: false
					});
				})
				.finally(() =>
					setState({
						loading: false
					})
				);
		},
		[id]
	);

	const handleOnNext = () => {
		handleOnLoadNextOrPrev({
			id: state.data?._id,
			equipmentId: state.data?.EquipmentId,
			type: 'sterilizerCycle',
			isIncrement: true
		});
	};
	const handleOnPrevious = () => {
		handleOnLoadNextOrPrev({
			id: state.data?._id,
			equipmentId: state.data?.EquipmentId,
			type: 'sterilizerCycle',
			isIncrement: false
		});
	};

	useEffect(() => {
		fetchData();
	}, [fetchData, id]);

	const reprocessXml = useCallback(() => {
		setState({ isXmlProcess: true });
		let formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'ReprocessXml');
		formData.append('id', id);
		util.fetchRequest({ url: connectionService.CycleList, formData: formData }, (response) => {
			if (response.status === 200) {
				Swal.fire({ titleText: strings.Success, text: strings.XmlReprocessed, icon: 'success', allowOutsideClick: false });
			} else {
				Swal.fire({ titleText: strings.Success, text: strings.XmlProcessingFailed, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
			}
			setState({ isXmlProcess: false });
		});
	}, [id]);

	const onOpenBIScreen = useCallback((incubatorCycleDetailId) => { }, []);

	const loadBiResultForm = useCallback(
		(id = 0, resultType = 'BIResult') => {
			const biCombos = [
				{ type: 'BILotNumber', loaded: true },
				{ type: 'CILotNumber', loaded: true }
			];
			setState({ loading: true });
			let formData = new FormData();
			formData.append('sterilizerId', state.data?.SterilizerId);
			formData.append('sterilizerCycleId', state.data?.SterilizerCycleId);
			formData.append('BIResult', state.data?.BIResult || 0);
			formData.append('BILotNumberId', state.data?.BILotNumber);
			formData.append('CIResult', state.data?.CIResult);
			formData.append('BILotNumberId', state.data?.BILotNumber);
			formData.append('CILotNumberId', state.data?.CILotNumberId);
			formData.append('id', id);
			formData.append('ExpiryDate', state.data?.ExpiryDate);
			formData.append('attachmentCount', state.data?.AttachmentCount || 0);
			formData.append('comboTypes', JSON.stringify(biCombos));
			formData.append('action', 'load');
			fetch(connectionService.IncubatorCycle, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then(({ data: record, combos }) => {
					setState({
						incubatorResultDialogTitle: strings[resultType],
						incubatorResultDialogOpen: true,
						incubatorResultActiveRecord: {
							record: {
								...record,
								id: id,
								CILotNumberId: state?.data?.CILotNumberId,
								CIResult: state?.data?.CIResult,
								BIResult: state?.data?.BIResult
							},
							id,
							combos,
							resultType,
							data: state.data
						}
					});
				})
				.catch((err) => {
					Swal(strings.Error, strings[`Status_${err?.status}`], 'error');
				})
				.finally(() =>
					setState({
						loading: false
					})
				);
		},
		[state, attachmentCount, sterilizerId]
	);

	const onAddBIResult = useCallback((type) => {
		loadBiResultForm(state?.data?.IncubatorCycleId || 0, type);
	}, [state]);

	const onCloseIncubatorResultDialog = () => {
		setState({ incubatorResultDialogOpen: false, incubatorResultActiveRecord: null });
		fetchData();
	};
	const onPrintTape = useCallback(() => {
		if (tapeRef.current?.download) {
			const { SerialNumber, CycleName, CycleCount, CycleStart } = state.data;
			const filename = `${SerialNumber}_${CycleName.replace(/ /g, '')}_${CycleCount}_${dayjs(CycleStart).format(
				consts.MomentFormat.MonthDateYear
			)}_Print_Tape`;
			setState({ loading: true });
			tapeRef.current.download(filename, 'pdf').finally(() => setState({ loading: false }));
		}
	}, [state.data]);


	const onPreviewTape = () => {
		if (tapeRef.current?.print) {
			tapeRef.current.print();
		}

	};

	const onPreviewDetails = useCallback(() => onPrintDetails(true), [state.data]);

	const cycleTapeDisplayed = state.data?.CycleTape?.length > 0;

	return (
		<SterilizerCycleInfoProvider
			value={{
				...state,
				cycleId,
				printProperties,
				isAMSCOEU,
				isSteamEU,
				isLeakTestCycle,
				isSteam,
				loadRelease,
				access,
				isParametric,
				isBiometric,
				bIResultColor,
				bIResultIcon,
				cIResultColor,
				cIResultIcon,
				cycleTapeDisplayed,
				isLeakTest,
				reprocessXml,
				onOpenCycleReviewFormDialog,
				onOpenCIResultFormDialog,
				onCloseCIResultFormDialog,
				onOpenXmlModal,
				onOpenChartPopUp,
				onPrintDetails,
				onPrintTape,
				onCloseXmlEditorDialog,
				onOpenBIScreen,
				onAddBIResult,
				onCloseIncubatorResultDialog,
				onCloseCycleReviewFormDialog,
				onPhasesFilter,
				onPreviewTape,
				onPreviewDetails,
				xmlFileName: state.data?.StorageFilename
			}}
		>
			{!!state.data && (
				<Grid container rowSpacing={2} size={12}>
					<CycleBreadcrumbs />
					<Grid size={12} container>
						<Card
							sx={{
								width: '100%',
								'& .MuiCardContent-root:last-child': {
									paddingBottom: '0px',
								}
							}}
						>
							<CardContent>
								<Grid container size={12}>
									<Grid size={12}>
										<NextPrevComponent onNext={handleOnNext} height={200} onPrev={handleOnPrevious}>
											<Box display="flex" columnGap={1} flexDirection={'row'}>
												<Box flexGrow={1}>
													<TopSection />
												</Box>
											</Box>
										</NextPrevComponent>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
					<Grid size={12} container>
						<Card sx={{ width: '100%' }}>
							<CardContent>
								<Grid container>
									<Grid size={12}>
										<Box
											sx={{
												display: 'grid',
												gridTemplateColumns: displayCycleTape ? '1fr' : '1fr auto',
												gridTemplateRows: '1fr',
												gridColumnGap: '20px'
											}}
										>
											<Box>
												<Grid container>
													<Grid size={12}>
														<CriticalParameters />
													</Grid>
													<Grid size={12}>
														<TrendsLineChart ref={stockChartRef} />
													</Grid>
												</Grid>
											</Box>

											{cycleTapeDisplayed && (
												<Box ref={tapeContainerRef}>
													<Receipt content={state.data?.CycleTape} ref={tapeRef} languageCode={state.data?.CycleLanguageCode} />
												</Box>
											)}
										</Box>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>

					<XmlEditorDialog
						xmlEditorOpen={state.xmlEditorOpen}
						xmlEditorValue={state.xmlEditorValue}
						xmlEditorTitle={state.xmlEditorTitle}
						xmlFileName={state.data?.StorageFilename}
						onCloseXmlEditorDialog={onCloseXmlEditorDialog}
					/>
					<IncubatorResultDialog />
					<CIResultFormDialog />
					<CycleReviewFormDialog
						data={state.data}
						open={state.cycleReviewFormDialogOpen}
						onDismiss={onCloseCycleReviewFormDialog}
					/>
				</Grid>
			)
			}

			<LoadingDialog open={state.loading} />
		</SterilizerCycleInfoProvider >
	);
};

export default SterilizerCycleInfo;
